const getLineHeight = ({ element }: { element: HTMLElement }): number => {
	const { lineHeight, fontSize } = window.getComputedStyle(element);

	return lineHeight.includes('px') ? +lineHeight.slice(0, -2) : +fontSize.slice(0, -2) * 1.2;
};

const processTextNode = (
	node: ChildNode,
	maxHeight: number,
	cumulativeHeight: number,
	visibleLinesNb: number,
): [number, number, boolean] => {
	const tempElement = document.createElement('span');
	tempElement.style.display = 'inline-block';
	tempElement.style.visibility = 'hidden';
	tempElement.textContent = node.textContent || '.';
	document.body.appendChild(tempElement);

	const elementHeight = tempElement.offsetHeight;
	const lineHeight = getLineHeight({ element: tempElement });
	const lineCount = Math.round(elementHeight / lineHeight) || 0;

	let localCumulative = cumulativeHeight;
	let localVisibleLines = visibleLinesNb;
	let isProcessingFinished = false;

	Array.from({ length: lineCount }).some(() => {
		if (localCumulative + lineHeight > maxHeight) {
			isProcessingFinished = true;

			return true;
		}

		localCumulative += lineHeight;
		localVisibleLines += 1;

		return false;
	});

	document.body.removeChild(tempElement);

	return [localCumulative, localVisibleLines, isProcessingFinished];
};

const processHTMLElementNode = (
	node: HTMLElement,
	maxHeight: number,
	cumulativeHeight: number,
	visibleLinesNb: number,
): [number, number, boolean] => {
	const style = window.getComputedStyle(node);
	const elementHeight = node.offsetHeight;
	const lineHeight = getLineHeight({ element: node });
	const lineCount = Math.round(elementHeight / lineHeight) || 0;

	let localCumulative = cumulativeHeight;
	let localVisibleLines = visibleLinesNb;
	let isProcessingFinished = false;

	Array.from({ length: lineCount }).some((_, index) => {
		let adjustedLineHeight = lineHeight;

		if (index === 0) {
			adjustedLineHeight += Math.round(parseFloat(style.marginTop)) + Math.round(parseFloat(style.paddingTop));
		}

		if (index === lineCount - 1) {
			adjustedLineHeight += Math.round(parseFloat(style.marginBottom)) + Math.round(parseFloat(style.paddingBottom));
		}

		if (localCumulative + adjustedLineHeight > maxHeight) {
			isProcessingFinished = true;

			return true;
		}

		localCumulative += adjustedLineHeight;
		localVisibleLines += 1;

		return false;
	});

	return [localCumulative, localVisibleLines, isProcessingFinished];
};

export const calculateVisibleLines = (container: HTMLElement, maxViewHeight: number): number => {
	const elements = Array.from(container.childNodes);
	const maxHeight = (window.innerHeight * maxViewHeight) / 100;

	let cumulativeHeight = 0;
	let visibleLinesNb = 0;

	elements.some((node) => {
		let result: [number, number, boolean] | null = null;

		if ((node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) || node.nodeName.toLowerCase() === 'br') {
			result = processTextNode(node, maxHeight, cumulativeHeight, visibleLinesNb);
		} else if (node instanceof HTMLElement) {
			result = processHTMLElementNode(node, maxHeight, cumulativeHeight, visibleLinesNb);
		}

		if (result) {
			const [newHeight, newLines, isProcessingFinished] = result;
			cumulativeHeight = newHeight;
			visibleLinesNb = newLines;

			return isProcessingFinished;
		}

		return false;
	});

	return visibleLinesNb;
};
